import { Card, Chip, IconButton, InputAdornment, Skeleton, Stack, TextField, Typography } from "@mui/material";
import { Close as CloseIcon, Search as SearchIcon } from "@mui/icons-material";
import dayjs from "dayjs";
import React, { useEffect, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  FETCH_TUTORIAL_VIDEOS,
  FETCH_TUTORIAL_VIDEOS_CATEGORIES,
  VISIBILITY_SCOPES
} from "../../constant/Apipath";
import { Postrequestcall } from "../../request/Postrequest";
import Loader from "../../common/Loader";
import TabsComponent from "../component/TabsComponent";
import { ClientEnglishFallback } from "../constant/ClientEnglishFallback";
import { TutorialViewVideosModal } from "./TutorialViewVideosModal";
import { VideoThumbnail } from "../videos/VideoThumbnail";

export default function TutorialVideos() {
  const { loginData, multiLanguageData } = useSelector((state) => state?.main);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [videosLoading, setVideosLoading] = useState(false);
  const [videosList, setVideosList] = useState([]);
  const [categoriesList, setCategoriesList] = useState([{ translateLabel: "All", categoryName: "All", id: 0 }]);
  const [value, setValue] = useState(0);
  const [viewVideos, setViewVideos] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [allDataLoaded, setAllDataLoaded] = useState(false);
  const [pageNr, setPageNr] = useState(1);
  const location = useLocation();
  const [searchText, setSearchText] = useState(location?.state?.searchText || "");
  const [debouncedSearchText, setDebouncedSearchText] = useState(location?.state?.searchText || "");
  const isFirstRender = useRef(true);

  const LANGUAGE = ClientEnglishFallback?.ClientVideos(multiLanguageData?.ClientVideos || {});
  const { TEXT_NO_VIDEOS } = LANGUAGE;

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    setSearchText(location?.state?.searchText || "");
    setDebouncedSearchText(location?.state?.searchText || "");
  }, [location?.state?.searchText]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText.trim());
    }, 500);

    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    setPageNr(1);
    fetchVideos(1);
  }, [debouncedSearchText]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setPageNr(1);
    fetchVideos(1);
  }, [value]);

  const fetchCategories = async () => {
    setCategoriesLoading(true);
    try {
      const payload = {
        PageNr: 1,
        NrOfRecPerPage: 20,
         FullSearch: debouncedSearchText || "",
         UserId: loginData?.userInformation?.UserId,
         IncludeRecordNr: true,
         TypeOfObjectReturned: "",
         SearchList: [
           {
             VisibilityScope: VISIBILITY_SCOPES.BI_FREE,
             Deleted: false
           }
         ],
        SortList: [
          {
            FieldName: "Id",
            Direction: "desc"
          }
        ]
      };

      const res = await Postrequestcall(FETCH_TUTORIAL_VIDEOS_CATEGORIES, payload, loginData?.accessToken);
      if (res?.status === 200) {
         const mappedCategories = (res?.data?.data || []).map((item) => ({
           id: item?.id,
           appId: item?.appId,
           categoryName: item?.appName,
           visibilityScope: item?.visibilityScope
         }));
        setCategoriesList([{ translateLabel: "All", categoryName: "All", id: 0 }, ...mappedCategories]);
      }
    } catch (error) {
      console.error("fetchCategories-> Error Getting Tutorial Video Categories: ", error);
    } finally {
      setCategoriesLoading(false);
    }
  };

  const fetchVideos = async (pageNo) => {
    try {
      if (pageNo === 1) {
        setVideosLoading(true);
      }
      setAllDataLoaded(true);
      const categoryId = value === 0 ? 0 : value;

      const payload = {
        PageNr: pageNo,
        NrOfRecPerPage: 20,
          FullSearch: debouncedSearchText || "",
          UserId: loginData?.userInformation?.UserId,
          IncludeRecordNr: true,
          TypeOfObjectReturned: "",
          SearchList: [
            {
              appScreenId: 2,
              CategoryId: categoryId,
              Deleted: false,
              isVisible: true,
              VisibilityScope: VISIBILITY_SCOPES.BI_FREE,
              ...(categoryId !== 0 && {
                visibilityScope: categoriesList.find((cat) => cat.id === categoryId)?.visibilityScope
              })
            }
          ],
        SortList: [
          {
            FieldName: "Id",
            Direction: "desc"
          }
        ]
      };

      const res = await Postrequestcall(FETCH_TUTORIAL_VIDEOS, payload, loginData?.accessToken);
      const resData = res?.data?.data ?? [];

      if (res?.status === 200) {
        if (pageNo === 1) {
          setVideosList(resData);
        } else {
          setVideosList((prevData) => [...prevData, ...resData]);
        }
        setAllDataLoaded(false);
      }
    } catch (error) {
      console.error("fetchVideos-> Error Fetching Tutorial Videos: ", error);
      setAllDataLoaded(false);
    } finally {
      setVideosLoading(false);
    }
  };

  const handleLoadMore = async () => {
    await fetchVideos(pageNr + 1);
    setPageNr((prevPage) => prevPage + 1);
  };

  const formatDuration = (duration) => {
    if (!duration) return "00:00";
    const parts = duration.split(":").map(Number);
    if (parts.length === 3) {
      const [h, m, s] = parts;
      if (h > 0) {
        return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
      }
      return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
    }
    if (parts.length === 2) {
      const [m, s] = parts;
      return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
    }
    if (parts.length === 1) {
      const [s] = parts;
      return `00:${s.toString().padStart(2, "0")}`;
    }
    return duration;
  };

  const formatSeconds = (durationInSeconds) => {
    if (durationInSeconds == null || Number.isNaN(Number(durationInSeconds))) return "00:00";
    const totalSeconds = Number(durationInSeconds);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  };

  const renderSkeletonCards = () =>
    Array.from({ length: 4 }).map((_, index) => (
      <Card
        key={`tutorial-skeleton-${index}`}
        sx={{
          padding: "10px",
          boxShadow: "none",
          borderRadius: "10px"
        }}>
        <Stack flexDirection={"row"} gap={1}>
          <Skeleton
            variant="rounded"
            sx={{
              width: {
                xs: "150px",
                sm: "150px",
                md: "250px",
                lg: "300px",
                xl: "300px"
              },
              height: {
                xs: "100px",
                sm: "100px",
                md: "150px",
                lg: "200px",
                xl: "200px"
              }
            }}
          />
          <Stack gap={1} p={1} sx={{ flex: 1 }}>
            <Skeleton variant="text" width="60%" height={32} />
            <Skeleton variant="text" width="30%" />
          </Stack>
        </Stack>
      </Card>
    ));

   const handleClearSearch = () => {
     setSearchText("");
     setDebouncedSearchText("");
   };

   return (
     <>
       <Stack direction="row" alignItems="center" gap={1} px={1} pb={1} pt={1}>
         <TextField
           size="small"
           fullWidth
           placeholder="Search videos"
           value={searchText}
           onChange={(e) => setSearchText(e.target.value)}
           onKeyDown={(e) => {
             if (e.key === "Enter") {
               setDebouncedSearchText(searchText.trim());
             }
           }}
           InputProps={{
             startAdornment: (
               <InputAdornment position="start">
                 <SearchIcon />
               </InputAdornment>
             ),
             endAdornment: searchText ? (
               <InputAdornment position="end">
                 <IconButton size="small" onClick={handleClearSearch}>
                   <CloseIcon fontSize="small" />
                 </IconButton>
               </InputAdornment>
             ) : null
           }}
         />
       </Stack>

       <TabsComponent
        setTabsValue={setValue}
        tabValue={value}
        tabs={categoriesList}
        label={"categoryName"}
        value={"id"}
        setPageNr={setPageNr}
        style={{
          opacity: categoriesLoading ? 0.7 : 1
        }}
      />

      <Stack gap={5} style={{ marginTop: "10px", height: "85vh", overflow: "auto" }} id="scrollablediv_prodcut">
        {videosLoading ? (
          <Stack spacing={2}>{renderSkeletonCards()}</Stack>
        ) : videosList?.length === 0 ? (
          <Stack
            sx={{
              width: "100%",
              height: "85vh",
              justifyContent: "center",
              alignItems: "center"
            }}>
            <Typography>{TEXT_NO_VIDEOS}</Typography>
          </Stack>
        ) : (
          <InfiniteScroll
            dataLength={videosList?.length}
            next={handleLoadMore}
            hasMore={true}
            loader={
              !allDataLoaded ? null : (
                <h4 style={{ display: "flex", justifyContent: "center" }}>
                  <Loader />
                </h4>
              )
            }
            scrollableTarget="scrollablediv_prodcut">
            {videosList.map((video, index) => (
              <Card
                onClick={() => {
                  setViewVideos(true);
                  setCurrentVideo(video);
                }}
                sx={{
                  cursor: "pointer",
                  padding: "10px",
                  boxShadow: "none",
                  m:1,
                  borderRadius: "4px"
                }}
                key={index}>
                <Stack flexDirection={"row"} gap={1}>
                  <div style={{ position: "relative" }}>
                    <VideoThumbnail
                      src={video?.thumbnailFileUrl || video?.thumbnail}
                      alt="video"
                      width={{
                        xs: "150px",
                        sm: "150px",
                        md: "250px",
                        lg: "300px",
                        xl: "300px"
                      }}
                      height={{
                        xs: "100px",
                        sm: "100px",
                        md: "150px",
                        lg: "200px",
                        xl: "200px"
                      }}
                    />
                    <Chip
                      sx={{
                        backgroundColor: "black",
                        color: "white",
                        borderRadius: "5px",
                        position: "absolute",
                        bottom: "10px",
                        right: "10px"
                      }}
                      size="small"
                      label={video?.duration ? formatDuration(video?.duration) : formatSeconds(video?.durationInSeconds)}
                    />
                  </div>
                  <Stack gap={1} p={1}>
                    <Typography fontWeight={"bold"} variant="h5">
                      {video?.title}
                    </Typography>
                    <Typography color="text.secondary" variant="body2" noWrap sx={{ maxWidth: "95%" }}>
                      {video?.description}
                    </Typography>
                    <Typography color="grey" variant="p">
                      {dayjs(video?.dateCreated).format("MMMM DD, YY")}
                    </Typography>
                  </Stack>
                </Stack>
              </Card>
            ))}
          </InfiniteScroll>
        )}
      </Stack>

      {viewVideos && (
        <TutorialViewVideosModal
          isOpen={viewVideos}
          onClose={() => {
            setViewVideos(false);
          }}
          data={currentVideo}
        />
      )}
    </>
  );
}
